import { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../config';
import { formatDate } from '../utils/formatDate';

// PERSONAL diagnostic tool — not linked from any nav/sidebar/permissions.
// Reachable only by navigating directly to /so-invoice-check. The Power
// Automate flow that auto-creates invoice_headers from invoice PDFs is
// occasionally offline, so so_headers.invoice_status can't always be
// trusted — this checks directly whether a real invoice_headers row exists
// for a given SO by matching PO / order ref no across both tables.

export default function SOInvoiceCheck({ user, onLogout }) {
  const [mode, setMode] = useState('search'); // 'search' | 'mismatch'

  const [query, setQuery] = useState('');
  const [sos, setSos] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [searched, setSearched] = useState(false);

  const [mismatches, setMismatches] = useState([]);
  const [missing, setMissing] = useState([]);
  const [mismatchLoading, setMismatchLoading] = useState(true);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function runSearch(e) {
    e?.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/acc-so-invoice-check?q=${encodeURIComponent(query.trim())}`);
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Search failed');
      setSos(data.sos || []);
      setInvoices(data.invoices || []);
      setSearched(true);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }

  const runMismatchCheck = useCallback(async () => {
    setMismatchLoading(true);
    setError('');
    try {
      const [mmRes, miRes] = await Promise.all([
        fetch(`${API_BASE}/acc-so-invoice-check?action=qty_mismatch`),
        fetch(`${API_BASE}/acc-so-invoice-check?action=missing_invoices`),
      ]);
      const mmData = await mmRes.json();
      const miData = await miRes.json();
      if (!mmRes.ok || !mmData.success) throw new Error(mmData.message || 'Check failed');
      if (!miRes.ok || !miData.success) throw new Error(miData.message || 'Check failed');
      setMismatches(mmData.mismatches || []);
      setMissing(miData.missing || []);
    } catch (err) {
      setError(err.message);
    }
    setMismatchLoading(false);
  }, []);

  // Discrepancies should show up automatically — no click needed.
  useEffect(() => { runMismatchCheck(); }, [runMismatchCheck]);

  const matchedRefs = new Set(invoices.map(inv => (inv.order_ref_no || '').trim().toUpperCase()).filter(Boolean));

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <div style={S.badge}>Personal tool — not linked anywhere</div>
          <h1 style={S.h1}>SO ⟷ Invoice Check</h1>
        </div>
        {onLogout && <button onClick={onLogout} style={S.btnGhost}>Log out</button>}
      </div>

      <div style={S.tabRow}>
        <button onClick={() => setMode('search')} style={mode === 'search' ? S.tabActive : S.tab}>Invoice Existence</button>
        <button onClick={() => setMode('mismatch')} style={mode === 'mismatch' ? S.tabActive : S.tab}>Qty Mismatch</button>
      </div>

      {error && <div style={{ ...S.banner, background: '#FBF1F3', color: '#C25E77' }}>{error}</div>}

      {mode === 'search' && (
        <>
          <div style={{ fontSize: 13, color: '#8C82A0', marginBottom: 14 }}>
            Search by PO / order ref no., SO voucher no., or invoice no. to see whether a real invoice exists.
          </div>

          <form onSubmit={runSearch} style={S.searchRow}>
            <input
              type="text"
              autoFocus
              placeholder="PO number / order ref no. / SO no. / invoice no."
              value={query}
              onChange={e => setQuery(e.target.value)}
              style={S.searchInput}
            />
            <button type="submit" disabled={loading} style={S.btnPrimary}>
              {loading ? 'Searching…' : 'Search'}
            </button>
          </form>

          {searched && !loading && (
            <>
              <div style={S.sectionTitle}>Matching SOs ({sos.length})</div>
              <div style={S.tableWrap}>
                <div style={S.tableHeaderRow}>
                  <div style={S.th}>SO No.</div>
                  <div style={S.th}>PO No.</div>
                  <div style={S.th}>Buyer</div>
                  <div style={S.th}>SO Date</div>
                  <div style={S.th}>Invoice Status</div>
                  <div style={S.th}>Real Invoice?</div>
                </div>
                {sos.map(so => {
                  const hasInvoice = matchedRefs.has((so.order_ref_no || '').trim().toUpperCase());
                  return (
                    <div key={so.voucher_no} style={S.tableRow}>
                      <div style={S.td}>{so.voucher_no}</div>
                      <div style={S.td}>{so.order_ref_no || '—'}</div>
                      <div style={S.td}>{so.buyer || so.consignee || '—'}</div>
                      <div style={S.td}>{formatDate(so.so_date)}</div>
                      <div style={S.td}>{so.invoice_status || '—'}</div>
                      <div style={S.td}>
                        <span style={hasInvoice ? S.pillGood : S.pillBad}>
                          {hasInvoice ? '✓ Found' : '✕ Not found'}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {sos.length === 0 && (
                  <div style={{ padding: 24, textAlign: 'center', color: '#9A90AE', fontSize: 13 }}>No matching SOs.</div>
                )}
              </div>

              <div style={{ ...S.sectionTitle, marginTop: 24 }}>Matching Invoices ({invoices.length})</div>
              <div style={S.tableWrap}>
                <div style={S.tableHeaderRow}>
                  <div style={S.th}>Invoice No.</div>
                  <div style={S.th}>PO No.</div>
                  <div style={S.th}>Buyer</div>
                  <div style={S.th}>Invoice Date</div>
                  <div style={{ ...S.th, textAlign: 'right' }}>Net Amount</div>
                </div>
                {invoices.map(inv => (
                  <div key={inv.invoice_no} style={S.tableRow}>
                    <div style={S.td}>{inv.invoice_no}</div>
                    <div style={S.td}>{inv.order_ref_no || '—'}</div>
                    <div style={S.td}>{inv.buyer || inv.consignee || '—'}</div>
                    <div style={S.td}>{formatDate(inv.invoice_date)}</div>
                    <div style={{ ...S.td, textAlign: 'right' }}>{inv.net_amount != null ? `₹${inv.net_amount}` : '—'}</div>
                  </div>
                ))}
                {invoices.length === 0 && (
                  <div style={{ padding: 24, textAlign: 'center', color: '#9A90AE', fontSize: 13 }}>No matching invoices.</div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {mode === 'mismatch' && (
        <>
          <div style={{ fontSize: 13, color: '#8C82A0', marginBottom: 14 }}>
            Discrepancies load automatically — qty mismatches (line items under-summing the SO header) and SOs with no
            matching invoice by PO/order ref no.
          </div>

          {mismatchLoading ? (
            <div style={{ padding: 24, color: '#8C82A0', fontSize: 13 }}>Loading…</div>
          ) : (
            <>
              <div style={S.sectionTitle}>SOs with under-counted quantity ({mismatches.length})</div>
              <div style={S.tableWrap}>
                <div style={{ ...S.tableHeaderRow, gridTemplateColumns: '1fr 1fr 1.3fr 1fr 0.9fr 0.9fr 0.9fr' }}>
                  <div style={S.th}>SO No.</div>
                  <div style={S.th}>PO No.</div>
                  <div style={S.th}>Buyer</div>
                  <div style={S.th}>SO Date</div>
                  <div style={{ ...S.th, textAlign: 'right' }}>Header Qty</div>
                  <div style={{ ...S.th, textAlign: 'right' }}>Line Item Sum</div>
                  <div style={{ ...S.th, textAlign: 'right' }}>Missing</div>
                </div>
                {mismatches.map(m => (
                  <div key={m.voucher_no} style={{ ...S.tableRow, gridTemplateColumns: '1fr 1fr 1.3fr 1fr 0.9fr 0.9fr 0.9fr' }}>
                    <div style={S.td}>{m.voucher_no}</div>
                    <div style={S.td}>{m.order_ref_no || '—'}</div>
                    <div style={S.td}>{m.buyer || m.consignee || '—'}</div>
                    <div style={S.td}>{formatDate(m.so_date)}</div>
                    <div style={{ ...S.td, textAlign: 'right' }}>{m.total_quantity}</div>
                    <div style={{ ...S.td, textAlign: 'right' }}>{m.line_qty_sum}</div>
                    <div style={{ ...S.td, textAlign: 'right', color: '#C25E77', fontWeight: 700 }}>
                      {m.total_quantity - m.line_qty_sum}
                    </div>
                  </div>
                ))}
                {mismatches.length === 0 && (
                  <div style={{ padding: 24, textAlign: 'center', color: '#9A90AE', fontSize: 13 }}>No mismatches found.</div>
                )}
              </div>

              <div style={{ ...S.sectionTitle, marginTop: 24 }}>Missing Invoices ({missing.length})</div>
              <div style={S.tableWrap}>
                <div style={S.tableHeaderRow}>
                  <div style={S.th}>SO No.</div>
                  <div style={S.th}>PO No.</div>
                  <div style={S.th}>Buyer</div>
                  <div style={S.th}>SO Date</div>
                  <div style={{ ...S.th, textAlign: 'right' }}>Qty</div>
                  <div style={S.th}></div>
                </div>
                {missing.map(m => (
                  <div key={m.voucher_no} style={S.tableRow}>
                    <div style={S.td}>{m.voucher_no}</div>
                    <div style={S.td}>{m.order_ref_no || '—'}</div>
                    <div style={S.td}>{m.buyer || m.consignee || '—'}</div>
                    <div style={S.td}>{formatDate(m.so_date)}</div>
                    <div style={{ ...S.td, textAlign: 'right' }}>{m.total_quantity}</div>
                    <div style={S.td}></div>
                  </div>
                ))}
                {missing.length === 0 && (
                  <div style={{ padding: 24, textAlign: 'center', color: '#9A90AE', fontSize: 13 }}>No missing invoices found.</div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

const S = {
  page: { maxWidth: 1100, margin: '0 auto', padding: '28px 24px', fontFamily: "'Poppins', sans-serif", color: '#231C30' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  badge: { display: 'inline-block', padding: '4px 10px', borderRadius: 20, background: '#EDE9F7', color: '#5B4B8A', fontSize: 11, fontWeight: 700, marginBottom: 8, letterSpacing: '0.03em' },
  h1: { fontSize: 26, fontWeight: 700, margin: 0 },
  banner: { padding: '10px 16px', borderRadius: 10, fontSize: 13, marginBottom: 16 },
  tabRow: { display: 'flex', gap: 8, marginBottom: 18, borderBottom: '1px solid #ECE7F2' },
  tab: { padding: '10px 16px', border: 'none', background: 'none', color: '#8C82A0', fontWeight: 600, fontSize: 13.5, cursor: 'pointer', fontFamily: "'Poppins'", borderBottom: '2px solid transparent' },
  tabActive: { padding: '10px 16px', border: 'none', background: 'none', color: '#6E58A6', fontWeight: 700, fontSize: 13.5, cursor: 'pointer', fontFamily: "'Poppins'", borderBottom: '2px solid #6E58A6' },
  searchRow: { display: 'flex', gap: 10, marginBottom: 20 },
  searchInput: { flex: 1, padding: '10px 14px', borderRadius: 9, border: '1px solid #E4DEEE', fontSize: 13.5, fontFamily: "'Poppins'" },
  sectionTitle: { fontSize: 14, fontWeight: 700, marginBottom: 10, color: '#231C30' },
  tableWrap: { borderRadius: 14, background: '#fff', border: '1px solid #ECE7F2', overflow: 'hidden' },
  tableHeaderRow: { display: 'grid', gridTemplateColumns: '1fr 1fr 1.3fr 1fr 1fr 1fr', background: '#231C30', color: '#fff' },
  tableRow: { display: 'grid', gridTemplateColumns: '1fr 1fr 1.3fr 1fr 1fr 1fr', borderBottom: '1px solid #F4F1F8', alignItems: 'center' },
  th: { padding: '12px 14px', fontSize: 11, fontWeight: 600, letterSpacing: '0.03em' },
  td: { padding: '12px 14px', fontSize: 13 },
  btnGhost: { padding: '9px 16px', borderRadius: 9, border: '1px solid #E4DEEE', background: '#fff', color: '#231C30', fontWeight: 500, fontSize: 13, cursor: 'pointer', fontFamily: "'Poppins'" },
  btnPrimary: { padding: '10px 20px', borderRadius: 9, border: 'none', background: '#6E58A6', color: '#fff', fontWeight: 600, fontSize: 13.5, cursor: 'pointer', fontFamily: "'Poppins'" },
  pillGood: { display: 'inline-block', padding: '3px 10px', borderRadius: 20, background: '#EAFAF0', color: '#158A45', fontSize: 11.5, fontWeight: 700 },
  pillBad: { display: 'inline-block', padding: '3px 10px', borderRadius: 20, background: '#FBF1F3', color: '#C25E77', fontSize: 11.5, fontWeight: 700 },
};
